import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Group,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
  Notification,
  BackgroundImage,
  useMantineColorScheme,
  useMantineTheme,
  PasswordInput,
  Modal,
  Loader,
  Accordion,
  ScrollArea,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import PreferencesComponent from "./preferences";
import HelpComponent from "./help";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useMe,
  useUpdateProfile,
  type User,
} from "@/api";
import { DateInput } from "@mantine/dates";
import { convertToBase64 } from "@/utility/file-utility";
import { getError } from "@/components/utility/utility/error-utility";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@/auth";
import { useQueryClient } from "@tanstack/react-query";
import { TermsAndConditionsContent } from "@/components/documents/terms-and-conditions";
import { PrivacyPolicyContent } from "@/components/documents/privacy-policy-content";
import { EULAContent } from "@/components/documents/eula-content";

// Define password validation schema for reuse
const passwordValidation = Yup.string()
  .min(8, "Password must be at least 8 characters long")
  .matches(/^(?=.*[A-Z])/, "Password must contain at least one uppercase letter")
  .matches(/^(?=.*[a-z])/, "Password must contain at least one lowercase letter")
  .matches(/^(?=.*[0-9])/, "Password must contain at least one number")
  .matches(/^(?=.*[@$!%*?&])/, "Password must contain at least one special character")
  .matches(/^\S+$/, "Password cannot contain spaces")
  .required("Password is required");

// Change Password Modal Component
interface ChangePasswordModalProps {
  opened: boolean;
  onClose: () => void;
}

interface ChangePasswordFormValues {
  old_password: string;
  new_password1: string;
  new_password2: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  opened,
  onClose,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();

  const formik = useFormik<ChangePasswordFormValues>({
    initialValues: {
      old_password: "",
      new_password1: "",
      new_password2: "",
    },
    validationSchema: Yup.object({
      old_password: Yup.string().required("Current password is required"),
      new_password1: passwordValidation,
      new_password2: Yup.string()
        .required("Confirm password is required")
        .oneOf([Yup.ref("new_password1")], "New password and confirm password must match"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await auth.changePassword(values.old_password, values.new_password1);
        

        
        // Log out user and redirect to login
        auth.signOut().then(() => {
          setTimeout(() => {
            navigate("/auth/login");
          }, 0);
        });
      } catch (error) {
        const { message } = getError(error as any);
        notifications.show({
          title: "Error",
          message: message || "Failed to change password",
          color: "red",
        });
      } finally {
        formik.resetForm();
        onClose();
        setIsSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Change Password"
      size="sm"
      centered
      radius="md"
      styles={{
        content: {
          background: colorScheme === "dark" 
            ? theme.colors.dark[7] 
            : "linear-gradient(90deg, #FFF8F6 0%, #FAF8FB 50%, #F5F8FA 100%)",
          maxWidth: "480px",
        },
        title: {
          color: colorScheme === "dark" ? "white" : undefined,
        },
        header: {
          borderBottom: `1px solid ${colorScheme === "dark" ? theme.colors.dark[4] : "#E9E9E9"}`,
          paddingBottom: "4px",
          marginBottom: "4px",
          backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : undefined,
        },
        body: {
          padding: "10px",
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack gap="xs">
          {/* Middle Card for Password Fields */}
          <Card 
            withBorder 
            radius="md" 
            p="xs" 
            bg={colorScheme === "dark" ? theme.colors.dark[8] : "white"}
            style={{
              borderColor: colorScheme === "dark" ? theme.colors.dark[4] : undefined,
            }}
          >
            <Stack gap="xs">
              <PasswordInput
                label="Current Password"
                placeholder="Enter Password"
                name="old_password"
                value={formik.values.old_password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.old_password && formik.errors.old_password}
                required
                styles={{
                  label: {
                    color: colorScheme === "dark" ? "white" : undefined,
                  },
                  input: {
                    backgroundColor: colorScheme === "dark" ? theme.colors.dark[8] : undefined,
                    color: colorScheme === "dark" ? "white" : undefined,
                  },
                }}
              />

              <PasswordInput
                label="New Password"
                placeholder="Enter Password"
                name="new_password1"
                value={formik.values.new_password1}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.new_password1 && formik.errors.new_password1}
                required
                styles={{
                  label: {
                    color: colorScheme === "dark" ? "white" : undefined,
                  },
                  input: {
                    backgroundColor: colorScheme === "dark" ? theme.colors.dark[8] : undefined,
                    color: colorScheme === "dark" ? "white" : undefined,
                  },
                }}
              />

              <PasswordInput
                label="Confirm Password"
                placeholder="Enter Password"
                name="new_password2"
                value={formik.values.new_password2}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.new_password2 && formik.errors.new_password2}
                required
                styles={{
                  label: {
                    color: colorScheme === "dark" ? "white" : undefined,
                  },
                  input: {
                    backgroundColor: colorScheme === "dark" ? theme.colors.dark[8] : undefined,
                    color: colorScheme === "dark" ? "white" : undefined,
                  },
                }}
              />
            </Stack>
          </Card>

          {/* Bottom Card for Buttons - aligned to modal bottom */}
          <Card 
            withBorder={false}
            radius="md"
            p="xs" 
            bg={colorScheme === "dark" ? theme.colors.dark[6] : "white"}
            style={{
              marginLeft: "-10px",
              marginRight: "-10px",
              marginBottom: "-10px",
              borderTop: `1px solid ${colorScheme === "dark" ? theme.colors.dark[4] : "#E9E9E9"}`,
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
            }}
          >
            <Group justify="flex-between" gap="xs">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                size="md"
                style={{
                  backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "white",
                  color: colorScheme === "dark" ? "white" : "#101828",
                  borderColor: colorScheme === "dark" ? theme.colors.dark[4] : "#E9E9E9",
                  width: "175px",
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={!formik.isValid || !formik.dirty}
                size="md"
                style={{
                  backgroundColor: "#124B8C",
                  color: "white",
                  width: "175px",
                }}
              >
                Save Changes
              </Button>
            </Group>
          </Card>
        </Stack>
      </form>
    </Modal>
  );
};

export default function SettingsPage() {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const [activeTab, setActiveTab] = useState<
    "profile" | "preferences" | "help" | "reference"
  >("profile");

  const tabBackgrounds: Record<string, string> = {
    profile: "/assets/carousel-image1.jpg",
    preferences: "/assets/EC_helth.jpg",
    help: "/assets/carousel-image3.jpg",
    reference: "/assets/carousel-image2.jpg",
  };

  const tabBackgroundPositions: Record<string, string> = {
    profile: "center 59%",
    preferences: "center 42%",
    help: "center 53%",
    reference: "center 55%",
  };

  const backgroundImageSrc = tabBackgrounds[activeTab] || tabBackgrounds["profile"];
  const backgroundPosition = tabBackgroundPositions[activeTab] || tabBackgroundPositions["profile"];

  return (
    <>
      <Card p={0} mb="md" radius="md" withBorder mx="sm" mt="md" ml="md" mr="md" >
        <BackgroundImage
          src={backgroundImageSrc}
          radius="md"
          style={{
            backgroundPosition: backgroundPosition,
            backgroundSize: "cover",
            height: 180,
            width: "100%",
          }}
          h={{ base: 160, sm: 180 }}
        >
          <Box
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              padding: "0.5rem 0.5rem 0.5rem 1.9rem",
              background: "linear-gradient(90deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)",
            }}
          >
            <Title
              order={1}
              c="#FAFAFA"
              size="xl"
              fw={700}
              style={{
                marginBottom: "0.75rem",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              Settings
            </Title>
            <Text
              c="#FAFAFA"
              size="md"
              fw={700}
              style={{
                opacity: 0.95,
                lineHeight: 1.4,
                maxWidth: "500px",
              }}
            >
              Customize your OneSelf Path experience
            </Text>
          </Box>
        </BackgroundImage>
      </Card>

      <Stack gap="md" mt={"sm"}  ml="md">
        <Tabs
          value={activeTab}
          onChange={(val) => setActiveTab(val as typeof activeTab)}
          defaultValue="profile"
          variant="pills"
        >
          <Tabs.List
bg={
  colorScheme === "dark"
    ? theme.colors.neutral[9]
    : theme.colors.neutral[2]
}            style={{ borderRadius: "10px", width: "fit-content" }}
            p="xs"
          >
            <Tabs.Tab 
              value="profile" 
              style={{
                borderRadius: "8px",
                backgroundColor: activeTab === "profile" ? "#124B8C" : "transparent",
                // color: activeTab === "profile" ? "white" : theme.colors.neutral[8],
              }}
              onMouseEnter={(e) => {
                if (activeTab !== "profile") {
                  e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                    ? "#555555"
                    : "white";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== "profile") {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              Profile
            </Tabs.Tab>
            <Tabs.Tab 
              value="preferences" 
              style={{
                borderRadius: "8px",
                backgroundColor: activeTab === "preferences" ? "#124B8C" : "transparent",
                // color: activeTab === "preferences" ? "white" : theme.colors.neutral[8],
              }}
              onMouseEnter={(e) => {
                if (activeTab !== "preferences") {
                  e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                    ? "#555555"
                    : "white";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== "preferences") {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            > 
              Preferences
            </Tabs.Tab>
            <Tabs.Tab 
              value="help" 
              style={{
                borderRadius: "8px",
                backgroundColor: activeTab === "help" ? "#124B8C" : "transparent",
                // color: activeTab === "help" ? "white" : theme.colors.neutral[8],
              }}
              onMouseEnter={(e) => {
                if (activeTab !== "help") {
                  e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                    ? "#555555"
                    : "white";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== "help") {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              Help
            </Tabs.Tab>
            <Tabs.Tab 
              value="reference" 
              style={{
                borderRadius: "8px",
                backgroundColor: activeTab === "reference" ? "#124B8C" : "transparent",
                // color: activeTab === "reference" ? "white" : theme.colors.neutral[8],
              }}
              onMouseEnter={(e) => {
                if (activeTab !== "reference") {
                  e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                    ? "#555555"
                    : "white";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== "reference") {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              Reference
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Stack>

      <Box p={{ base: "xs", sm: "md" }}>
        {activeTab === "profile" && <ProfileTab />}
        {activeTab === "preferences" && <PreferencesTab />}
        {activeTab === "help" && <HelpTab />}
        {activeTab === "reference" && <ReferenceTab />}
      </Box>
    </>
  );
}

function ProfileTab() {
  const { colorScheme } = useMantineColorScheme();
  const { data: userData, refetch } = useMe();
  const queryClient = useQueryClient();

  const { mutateAsync: updateUser } = useUpdateProfile();

  const [showNotification, setShowNotification] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [changePasswordModalOpened, setChangePasswordModalOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik<User>({
    initialValues: {
      id: 0,
      password: userData?.password ?? "",
      uuid: "",
      created_at: "",
      updated_at: "",
      first_name: userData?.first_name ?? "",
      middle_name: userData?.middle_name ?? "",
      last_name: userData?.last_name ?? "",
      email: userData?.email ?? "",
      phone_number: userData?.phone_number ?? "",
      date_of_birth: userData?.date_of_birth ?? "",
      address: userData?.address ?? {
        street_address: "",
        city: "",
        state: "",
        country: "",
        zip_code: "",
      },
      profile_picture: userData?.profile_picture ?? "",
      is_patient: userData?.is_patient ?? false,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      console.log(values);
      setIsLoading(true);
      try {
        await updateUser({
          data: {
            ...values,
          },
        });
        setShowNotification(true);
        // Clear selected image after successful update
        setSelectedImage("");
        setSelectedFileName("");
        await queryClient.invalidateQueries({ queryKey: ["users", "me"] });
        refetch();
      } catch (error) {
        console.error("Error updating profile:", error);
      } finally {
        setIsLoading(false);
      }
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .required("First name is required")
        .matches(/^[A-Za-z]+$/, "First name must contain only alphabetic characters (A-Z, a-z)")
        .min(1, "First name cannot be empty")
        .max(255, "First name cannot exceed 255 characters")
        .test("no-whitespace", "First name cannot contain whitespace", (value) => {
          if (!value) return true; // Let required validation handle empty values
          return !/\s/.test(value);
        })
        .test("no-underscore", "First name cannot contain underscore", (value) => {
          if (!value) return true; // Let required validation handle empty values
          return !value.includes("_");
        })
        .label("First Name"),
      middle_name: Yup.string()
        .matches(/^[A-Za-z]*$/, "Middle name must contain only alphabetic characters (A-Z, a-z)")
        .test("no-whitespace", "Middle name cannot contain whitespace", (value) => {
          if (!value) return true; // Allow empty middle name
          return !/\s/.test(value);
        })
        .test("no-underscore", "Middle name cannot contain underscore", (value) => {
          if (!value) return true; // Allow empty middle name
          return !value.includes("_");
        })
        .max(255, "Middle name cannot exceed 255 characters")
        .label("Middle Name"),
      last_name: Yup.string()
        .required("Last name is required")
        .matches(/^[A-Za-z]+$/, "Last name must contain only alphabetic characters (A-Z, a-z)")
        .min(1, "Last name cannot be empty")
        .max(255, "Last name cannot exceed 255 characters")
        .test("no-whitespace", "Last name cannot contain whitespace", (value) => {
          if (!value) return true; // Let required validation handle empty values
          return !/\s/.test(value);
        })
        .test("no-underscore", "Last name cannot contain underscore", (value) => {
          if (!value) return true; // Let required validation handle empty values
          return !value.includes("_");
        })
        .label("Last Name"),
      date_of_birth: Yup.date()
        .max(new Date(), "Date of birth cannot be in the future")
        .min(new Date('1900-01-01'), "Date of birth cannot be before 1900")
        .nullable()
        .label("Date of Birth"),
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email is required")
        .matches(/^[^\s]+$/, "Email cannot contain spaces")
        .matches(/^[a-zA-Z0-9@._-]+$/, "Email contains unsupported symbols")
        .label("Email"),
      phone_number: Yup.string()
        .test("no-alphabets", "Phone number cannot contain alphabets", (value) => {
          if (!value) return true; // Allow empty
          return !/[a-zA-Z]/.test(value);
        })
        .test("no-spaces", "Phone number cannot contain spaces", (value) => {
          if (!value) return true; // Allow empty
          return !/\s/.test(value);
        })
        .test("no-hyphens", "Phone number cannot contain hyphens", (value) => {
          if (!value) return true; // Allow empty
          return !value.includes("-");
        })
        .test("no-special-chars", "Phone number cannot contain special characters", (value) => {
          if (!value) return true; // Allow empty
          return !/[^0-9+]/.test(value); // Only allow digits and plus sign
        })
        .max(255, "Phone number cannot exceed 255 characters")
        .label("Phone Number"),
      "address.zip_code": Yup.string()
        .matches(
          /^[1-9]\d*$/,
          "Zip code must contain only numeric digits and cannot start with zero"
        )
        .test("length", "Zip code must be 4 to 5 digits", (value) => {
          if (!value) return true; // Allow empty
          return value.length >= 4 && value.length <= 5;
        })
        .test("no-spaces", "Zip code cannot contain spaces", (value) => {
          if (!value) return true; // Allow empty
          return !/\s/.test(value);
        })
        .label("Zip Code"),
    }),
  });

  const handleChangePassword = () => {
    setChangePasswordModalOpened(true);
  };

  return (
    
    <Box style={{ maxWidth: "100%", width: "100%", position: "relative" }}>
      {isLoading && (
        <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <Stack align="center" gap="md">
            <Loader color="blue" size="lg" />
            <Text size="sm" c="dimmed">
              Saving profile...
            </Text>
          </Stack>
        </Box>
      )}
      
      <Card radius="md" p={{ base: "sm", sm: "md" }} withBorder>
      {showNotification && (
        <Notification
          color="teal"
          mt="md"
          onClose={() => setShowNotification(false)}
        >
          User Data Updated Successfully
        </Notification>
      )}
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Title order={4} mb="md" style={{ flex: 1, minWidth: 200 }}>
          Profile
        </Title>

        <Button 
          onClick={() => formik.handleSubmit()} 
          size="md"
        >
          Save
        </Button>
      </Group>
      <Grid mt={"md"}>
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Dropzone
            onDrop={async (files: File[]) => {
              if (files.length > 0) {
                const base64String = await convertToBase64(files[0]);
                formik.setFieldValue("profile_picture", base64String);
                setSelectedFileName(files[0].name);
                setSelectedImage(base64String);
              }
            }}
            accept={['image/jpeg', 'image/png', 'image/jpg']}
            maxSize={10 * 1024 * 1024} // 10MB
          >
            <Box p={{ base: "md", sm: "xl" }} style={{ textAlign: "center" }}>
              <Text size="md">Upload Profile Picture</Text>
              {selectedImage ? (
                <Box mt="md">
                  <img 
                    src={selectedImage} 
                    alt="Selected profile" 
                    style={{ 
                      width: window.innerWidth < 768 ? '80px' : '120px', 
                      height: window.innerWidth < 768 ? '80px' : '120px', 
                      objectFit: 'cover',
                      borderRadius: '50%',
                      border: '2px solid #ddd'
                    }} 
                  />
                  <Text size="sm" mt={5} style={{ 
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                  }}>
                    {selectedFileName}
                  </Text>
                </Box>
              ) : userData?.profile_picture ? (
                <Box mt="md">
                  <img 
                    src={userData.profile_picture} 
                    alt="Current profile" 
                    style={{ 
                      width: window.innerWidth < 768 ? '80px' : '120px', 
                      height: window.innerWidth < 768 ? '80px' : '120px', 
                      objectFit: 'cover',
                      borderRadius: '50%',
                      border: '2px solid #ddd'
                    }} 
                  />
                  <Text size="xs" color="dimmed" mt={5}>
                    Current Profile Picture
                  </Text>
                </Box>
              ) : (
                <Text size="xs" color="dimmed">
                  Max File Size: 10MB <br />
                  Supported files: jpg, png
                </Text>
              )}
            </Box>
          </Dropzone>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 9 }}>
          <Stack>
            <Card withBorder radius={5} p={{ base: "sm", sm: "md" }}>
              <Text fw={600} size="md">Basic Info</Text>
              <Stack gap="md" mt="md">
                <Group grow align="flex-start" gap="md">
                  <TextInput
                    label="First Name"
                    placeholder="First Name"
                    value={formik.values.first_name ?? ""}
                    name="first_name"
                    onChange={formik.handleChange}
                    error={formik.touched.first_name && formik.errors.first_name}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                  <TextInput
                    label="Middle Name"
                    placeholder="Middle Name"
                    value={formik.values.middle_name ?? ""}
                    name="middle_name"
                    onChange={formik.handleChange}
                    error={formik.touched.middle_name && formik.errors.middle_name}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                  <TextInput
                    label="Last Name"
                    placeholder="Last Name"
                    value={formik.values.last_name ?? ""}
                    name="last_name"
                    onChange={formik.handleChange}
                    error={formik.touched.last_name && formik.errors.last_name}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                </Group>
                <Group grow align="flex-start" gap="md">
                  <DateInput
                    label="Date of Birth"
                    placeholder="MM/DD/YYYY"
                    value={formik.values.date_of_birth ?? ""}
                    name="date_of_birth"
                    id="profile_date_of_birth-input"
                    key="profile_date_of_birth-input"
                    onChange={(date) => {
                      formik.setFieldValue("date_of_birth", date);
                    }}
                    size="md"
                    // type="date"
                    autoComplete="bday"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                  <TextInput
                    label="Email"
                    placeholder="Email"
                    value={formik.values.email ?? ""}
                    name="email"
                    onChange={formik.handleChange}
                    error={formik.touched.email && formik.errors.email}
                    size="md"
                    disabled={true}
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                  <PasswordInput
                    label="Password"
                    placeholder="Enter Password"
                    value={formik.values.password ?? ""}
                    name="password"
                    onChange={formik.handleChange}
                    error={formik.touched.password && formik.errors.password}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem",
                        caretColor: colorScheme === "dark" ? "#ffffff" : "#000000"
                      },
                      innerInput: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                </Group>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button 
                    variant="subtle" 
                    color="blue" 
                    onClick={handleChangePassword}
                    size="sm"
                  >
                    Change Password
                  </Button>
                </div>
              </Stack>
            </Card>

            <Card withBorder radius={5} p={{ base: "sm", sm: "md" }}>
              <Title order={5} mb={15} size="md">
                Address Info
              </Title>

              <Stack gap="md" mt="md">
                <Group grow align="flex-start" gap="md">
                  <TextInput
                    name="phone_number"
                    label="Phone Number"
                    placeholder="Phone Number"
                    value={formik.values.phone_number ?? ""}
                    onChange={formik.handleChange}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                  <TextInput
                    name="address.street_address"
                    label="Street Address"
                    placeholder="Street Address"
                    value={formik.values.address?.street_address ?? ""}
                    onChange={(e) => {
                      formik.setFieldValue("address", {
                        ...formik.values.address,
                        street_address: e.target.value,
                      });
                    }}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                  <TextInput
                    name="address.city"
                    label="City"
                    placeholder="City"
                    value={formik.values.address?.city ?? ""}
                    onChange={(e) => {
                      formik.setFieldValue("address", {
                        ...formik.values.address,
                        city: e.target.value,
                      });
                    }}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                </Group>
                <Group grow align="flex-start" gap="md">
                  <TextInput
                    name="address.state"
                    label="State"
                    placeholder="State"
                    value={formik.values.address?.state ?? ""}
                    onChange={(e) => {
                      formik.setFieldValue("address", {
                        ...formik.values.address,
                        state: e.target.value,
                      });
                    }}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                  <TextInput
                    name="address.zip_code"
                    label="Zip Code"
                    placeholder="Zip Code"
                    value={formik.values.address?.zip_code ?? ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                      formik.setFieldValue("address", {
                        ...formik.values.address,
                        zip_code: value,
                      });
                    }}
                    onBlur={formik.handleBlur}
                    maxLength={5}
                    error={
                      (formik.touched.address as any)?.zip_code &&
                      (formik.errors.address as any)?.zip_code
                    }
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                  <TextInput
                    name="address.country"
                    label="Country"
                    placeholder="Country"
                    value={formik.values.address?.country ?? ""}
                    onChange={(e) => {
                      formik.setFieldValue("address", {
                        ...formik.values.address,
                        country: e.target.value,
                      });
                    }}
                    size="md"
                    styles={{
                      label: { 
                        marginBottom: 8, 
                        fontSize: "0.875rem", 
                        fontWeight: 500,
                        color: colorScheme === "dark" ? "#c1c2c5" : "#495057",
                        lineHeight: 1.2,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "flex-end"
                      },
                      input: { 
                        height: 40,
                        fontSize: "0.9rem"
                      }
                    }}
                  />
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Change Password Modal */}
      <ChangePasswordModal
        opened={changePasswordModalOpened}
        onClose={() => setChangePasswordModalOpened(false)}
      />
      </Card>
    </Box>
  );
}

function PreferencesTab() {
  return (
    <Card radius="md" p={{ base: "sm", sm: "md" }} withBorder>
      <PreferencesComponent />
    </Card>
  );
}

function HelpTab() {
  return (
    <Box p={{ base: "xs", sm: "sm" }}>
      <HelpComponent />
    </Box>
  );
}

function ReferenceTab() {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();

  return (
    <Card radius="md" p={{ base: "sm", sm: "md" }} withBorder>
      <Title order={4} size="md" mb="md">Reference </Title>
      <style>{`
        .mantine-Accordion-item[data-active] .mantine-Accordion-control {
          background-color: ${colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.neutral[0]} !important;
        }
        .mantine-Accordion-control:hover {
          background-color: ${colorScheme === "dark" ? theme.colors.dark[6] : "#F1F7FE"} !important;
        }
      `}</style>
      <Accordion
        variant="separated"
        radius="md"
        styles={{
          item: {
            border: `1px solid ${colorScheme === "dark" ? theme.colors.dark[4] : "#E9E9E9"}`,
            backgroundColor: colorScheme === "dark" ? theme.colors.dark[7] : "white",
          },
          control: {
            padding: "3px 20px",
            "&:hover": {
              backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "#F1F7FE",
            },
          },
          label: {
            fontFamily: "SF Pro",
            fontWeight: 510,
            fontSize: "16px",
            color: colorScheme === "dark" ? "white" : "#1D1D1D",
          },
          content: {
            padding: 0,
          },
        }}
      >
        <Accordion.Item value="terms-and-conditions">
          <Accordion.Control>
            Terms and Conditions
          </Accordion.Control>
          <Accordion.Panel>
            <ScrollArea
              style={{
                height: "500px",
                padding: "20px",
                paddingRight: "16px",
              }}
            >
              <TermsAndConditionsContent />
            </ScrollArea>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="privacy-policy">
          <Accordion.Control>
            Privacy Policy
          </Accordion.Control>
          <Accordion.Panel>
            <ScrollArea
              style={{
                height: "500px",
                padding: "20px",
                paddingRight: "16px",
              }}
            >
              <PrivacyPolicyContent />
            </ScrollArea>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="eula">
          <Accordion.Control>
            End-User License Agreement
          </Accordion.Control>
          <Accordion.Panel>
            <ScrollArea
              style={{
                height: "500px",
                padding: "20px",
                paddingRight: "16px",
              }}
            >
              <EULAContent />
            </ScrollArea>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Card>
  );
}
